import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from '@reach/router'
import { generateRandomId } from 'src/utils/domHelper'
import * as Styles from './embeddedContent.module.scss'

enum EmbeddedContentType {
  webpage,
  pdf,
  video,
  flipsnack,
  cataboom,
}

export type EmbeddedContentContent = {
  anchor?: string
  url: string
  title: string
}

type Props = {
  content: EmbeddedContentContent
}

const EmbeddedContent = ({ content }: Props) => {
  const { url, anchor = generateRandomId(), title } = content
  const location = useLocation()

  // Extract AccountID and HFN from URL query params for CataBoom integration
  const { accountId, hfn } = useMemo(() => {
    const params = new URLSearchParams(location.search)
    return {
      accountId: params.get('AccountID'),
      hfn: params.get('HFN'),
    }
  }, [location.search])

  if (!url) return null

  let wrapperClass = Styles.contentWrapper
  let embededType = EmbeddedContentType.webpage

  if (url.endsWith('.pdf')) {
    embededType = EmbeddedContentType.pdf
  } else if (url.indexOf('flipsnack') > -1) {
    wrapperClass += ` ${Styles.flipsnack}`
    embededType = EmbeddedContentType.flipsnack
  } else if (url.indexOf('cataboom') > -1) {
    wrapperClass += ` ${Styles.cataboom}`
    embededType = EmbeddedContentType.cataboom
  } else if (
    url.indexOf('youtube.com/embed/') > -1 ||
    url.indexOf('youtu.be') > -1
  ) {
    wrapperClass += ` ${Styles.video}`
    embededType = EmbeddedContentType.video
  }

  // Build CataBoom URL with AccountID and HFN from query params (if available)
  const cataboomUrl = useMemo(() => {
    if (embededType !== EmbeddedContentType.cataboom) {
      return null
    }

    // encodeURIComponent prevents URL injection via special characters
    const params = new URLSearchParams()
    if (hfn) params.set('HFN', hfn)
    if (accountId) params.set('AccountID', accountId)

    const query = params.toString()
    if (!query) return url

    const separator = url.includes('?') ? '&' : '?'
    return `${url}${separator}${query}`
  }, [url, accountId, hfn, embededType])

  // iframe management
  const iframeRef = useRef<HTMLIFrameElement>(null)

  // Defer rendering the CataBoom iframe until after client mount so the src
  // includes query params on first render. Otherwise SSR emits the iframe
  // without params, the browser starts loading it, and hydration swaps in a
  // new src — causing the iframe to load twice in production.
  const [isMounted, setIsMounted] = useState(false)
  useEffect(() => {
    setIsMounted(true)
  }, [])

  useEffect(() => {
    const iframe = iframeRef.current

    const handleIframeMessage = (e: MessageEvent) => {
      if (
        e.source === iframe?.contentWindow &&
        e.data.type === 'formHeightChanged'
      ) {
        const newHeight = e.data.height
        iframe.style.height = `${newHeight}px`
      }
    }

    window.addEventListener('message', handleIframeMessage)

    return () => {
      window.removeEventListener('message', handleIframeMessage)
    }
  }, [])

  // Render

  return (
    <section id={anchor} className={Styles.embeddedContent}>
      <div className={wrapperClass}>
        {embededType === EmbeddedContentType.webpage && (
          <iframe ref={iframeRef} title={title} src={url} />
        )}
        {embededType === EmbeddedContentType.pdf && (
          <iframe
            scrolling="no"
            title={title}
            src={`https://docs.google.com/viewerng/viewer?url=${url}&embedded=true#toolbar=0&view=FitH`}
          />
        )}
        {embededType === EmbeddedContentType.flipsnack && (
          <iframe
            scrolling="no"
            title={title}
            src={`${url}#toolbar=0&view=FitH`}
          />
        )}
        {embededType === EmbeddedContentType.video && (
          <iframe
            scrolling="no"
            title={title}
            src={`${url}#toolbar=0&view=FitH`}
            data-src={`${url}#toolbar=0&view=FitH`}
            className="optanon-category-0005"
          />
        )}
        {embededType === EmbeddedContentType.cataboom &&
          cataboomUrl &&
          isMounted && (
            <iframe ref={iframeRef} title={title} src={cataboomUrl} />
          )}
      </div>
    </section>
  )
}

export default EmbeddedContent
